import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../components/Sidebar/Sidebar';
import { Modal, Button, Box, TextField, MenuItem } from '@mui/material';
import './DetalhesProcesso.css';

const apiUrl = process.env.REACT_APP_API_URL;

function DetalhesProcesso() {
  const { id } = useParams();
  const [processoDetalhes, setProcessoDetalhes] = useState(null);
  const [analistas, setAnalistas] = useState([]);
  const [filteredAnalistas, setFilteredAnalistas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [analistaModalOpen, setAnalistaModalOpen] = useState(false);
  const [novoAnalista, setNovoAnalista] = useState({ analista_id: '', sla_meta: '' });
  const [todosAnalistas, setTodosAnalistas] = useState([]);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(''); // Estado para a data

  // Estado para ordenação
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

  // Função para calcular a data atual e a semana
  const calculateDateInfo = () => {
    const now = new Date();
    const day = now.getDate().toString().padStart(2, '0');
    const month = now.toLocaleString('default', { month: 'long' });
    const year = now.getFullYear();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const weekNumber = Math.ceil((now.getDate() + firstDay.getDay()) / 7);

    return `${day}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${year} | ${weekNumber}° Semana de ${month} de ${year}`;
  };

  useEffect(() => {
    setCurrentDate(calculateDateInfo()); // Atualiza a data
    axios
      .get(`${apiUrl}/servicos/${id}/detalhes`)
      .then((response) => setProcessoDetalhes(response.data))
      .catch((error) => console.error('Erro ao buscar detalhes do processo:', error));

    axios
      .get(`${apiUrl}/servicos/${id}/analistas`)
      .then((response) => {
        const analistasCorrigidos = response.data.map((analista) => ({
          ...analista,
          ch_trabalho_alcancada: analista.ch_trabalho_alta, // Corrige o nome para o front-end
        }));
        setAnalistas(analistasCorrigidos);
        setFilteredAnalistas(analistasCorrigidos);
      })
      .catch((error) => console.error('Erro ao buscar analistas associados:', error));

    axios
      .get(`${apiUrl}/analistas`)
      .then((response) => setTodosAnalistas(response.data))
      .catch((error) => console.error('Erro ao buscar todos os analistas:', error));
  }, [id]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedAnalistas = [...filteredAnalistas].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setFilteredAnalistas(sortedAnalistas);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕'; // Ícone padrão para indicar que a coluna é ordenável
    return sortConfig.direction === 'asc' ? '🔼' : '🔽'; // Ícones para ascendente e descendente
  };

  const handleAddAnalista = () => {
    axios
      .post(`${apiUrl}/servicos/${id}/analistas`, novoAnalista)
      .then((response) => {
        const novoAnalistaCorrigido = {
          ...response.data,
          ch_trabalho_alcancada: response.data.ch_trabalho_alta, // Corrige o campo
        };
        setAnalistas((prev) => [...prev, novoAnalistaCorrigido]);
        setFilteredAnalistas((prev) => [...prev, novoAnalistaCorrigido]);
        setAnalistaModalOpen(false);
        setNovoAnalista({ analista_id: '', sla_meta: '' });
      })
      .catch((error) => console.error('Erro ao adicionar analista:', error));
  };

  const calculateDerivedFields = (produtividadeEntregue, slaEntrega) => {
    const produtividadeMeta = processoDetalhes.produtividade_meta || 0;
    const cargaHoraria = processoDetalhes.carga_horaria || 1;

    const produtividadeReal =
      produtividadeEntregue && produtividadeMeta
        ? (produtividadeEntregue / produtividadeMeta) * 100
        : 0;

    const chTrabalhoAlcancada =
      produtividadeEntregue && slaEntrega
        ? (produtividadeEntregue * slaEntrega) / 60
        : 0;

    const taxaOcupacao =
      chTrabalhoAlcancada && cargaHoraria ? (chTrabalhoAlcancada / cargaHoraria) * 100 : 0;

    return {
      produtividadeReal,
      chTrabalhoAlcancada,
      taxaOcupacao,
    };
  };

  const getFarolColor = (taxaOcupacao) => {
    if (taxaOcupacao >= 75) return 'green';
    if (taxaOcupacao >= 50) return 'yellow';
    return 'red';
  };

  const handleInputChange = (analistaId, field, value) => {
    const updatedAnalistas = analistas.map((analista) => {
      if (analista.id === analistaId) {
        const updatedAnalista = { ...analista, [field]: value };

        if (field === 'produtividade_entregue' || field === 'sla_entrega') {
          const { produtividadeReal, chTrabalhoAlcancada, taxaOcupacao } =
            calculateDerivedFields(
              parseFloat(updatedAnalista.produtividade_entregue) || 0,
              parseFloat(updatedAnalista.sla_entrega) || 0
            );

          updatedAnalista.produtividade_real = produtividadeReal.toFixed(2);
          updatedAnalista.ch_trabalho_alcancada = chTrabalhoAlcancada.toFixed(2);
          updatedAnalista.taxa_ocupacao = taxaOcupacao.toFixed(2);
        }

        return updatedAnalista;
      }
      return analista;
    });

    setAnalistas(updatedAnalistas);
    setFilteredAnalistas(updatedAnalistas);
  };

  const handleUpdateCell = (analistaId, field, value) => {
    const updatedAnalista = analistas.find((analista) => analista.id === analistaId);
    const payload = {
      ...updatedAnalista,
      [field]: value,
      ch_trabalho_alta: parseFloat(updatedAnalista.ch_trabalho_alcancada) || null, // Envia o campo correto para a API
      produtividade_real: parseFloat(updatedAnalista.produtividade_real) || null,
      taxa_ocupacao: parseFloat(updatedAnalista.taxa_ocupacao) || null,
    };
    console.log('Enviando payload:', payload);
    axios
      .put(`${apiUrl}/analistas-servicos/${analistaId}`, payload)
      .then(() => console.log('Atualizado com sucesso'))
      .catch((error) => console.error('Erro ao atualizar analista:', error));
  };

  const handleSearchChange = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = analistas.filter((analista) =>
      analista.analista_name.toLowerCase().includes(value)
    );
    setFilteredAnalistas(filtered);
  };

  if (!processoDetalhes) {
    return <p>Carregando...</p>;
  }

  return (
    <div className="detalhes-container">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setSidebarOpen(!isSidebarOpen)} />
      <div className={`content ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <div className="processo-header">
          <div className="info-group">
            <p><strong>Nome:</strong> {processoDetalhes.name}</p>
            <p><strong>Tipo:</strong> {processoDetalhes.tipo_name}</p>
          </div>
          <div className="info-group">
            <p><strong>Gestor:</strong> {processoDetalhes.gestor_name}</p>
            <p><strong>E2E:</strong> {processoDetalhes.e2e_name}</p>
          </div>
          <div className="info-group">
            <p><strong>Produtividade Meta:</strong> {processoDetalhes.produtividade_meta}</p>
            <p><strong>Carga Horária:</strong> {processoDetalhes.carga_horaria}</p>
          </div>
        </div>
        <div className="analistas-section">
          <div className="analistas-header">
            <TextField
              label="Pesquisar Analista"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-bar"
            />
            <div className="date-info">
            <p><strong>Data:</strong> {currentDate}</p>
          </div>
            <Button
              variant="contained"
              onClick={() => setAnalistaModalOpen(true)}
              className="add-analista-button"
            >
              + Adicionar
            </Button>
          </div>
          <table className="analistas-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('analista_name')}>
                  Analista {getSortIcon('analista_name')}
                </th>
                <th onClick={() => handleSort('produtividade_entregue')}>
                  Produtividade Entregue {getSortIcon('produtividade_entregue')}
                </th>
                <th className="table-header" onClick={() => handleSort('produtividade_real')}>
                  Produtividade <br /> Real (%) {getSortIcon('produtividade_real')}
                </th>
                <th onClick={() => handleSort('sla_meta')}>
                  SLA Meta {getSortIcon('sla_meta')}
                </th>
                <th onClick={() => handleSort('sla_entrega')}>
                  SLA Entrega {getSortIcon('sla_entrega')}
                </th>
                <th onClick={() => handleSort('ch_trabalho_alcancada')}>
                  CH Trabalho Alcançada {getSortIcon('ch_trabalho_alcancada')}
                </th>
                <th onClick={() => handleSort('taxa_ocupacao')}>
                  Taxa de Ocupação(%) {getSortIcon('taxa_ocupacao')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAnalistas.map((analista) => (
                <tr key={analista.id}>
                  <td>{analista.analista_name || 'Desconhecido'}</td>
                  <td>
                    <input
                      type="text"
                      value={analista.produtividade_entregue || ''}
                      onChange={(e) =>
                        handleInputChange(analista.id, 'produtividade_entregue', e.target.value)
                      }
                      onBlur={(e) =>
                        handleUpdateCell(analista.id, 'produtividade_entregue', e.target.value)
                      }
                    />
                  </td>
                  <td>{analista.produtividade_real || 0}%</td>
                  <td>{analista.sla_meta || 0}</td>
                  <td>
                    <input
                      type="text"
                      value={analista.sla_entrega || ''}
                      onChange={(e) =>
                        handleInputChange(analista.id, 'sla_entrega', e.target.value)
                      }
                      onBlur={(e) => {
                        console.log('Atualizando sla_entrega:', e.target.value);
                        handleUpdateCell(analista.id, 'sla_entrega', e.target.value);
                      }}
                    />
                  </td>
                  <td>{analista.ch_trabalho_alcancada || 0}</td>
                  <td>{Math.round(analista.taxa_ocupacao || 0)}%  <span
                      className="farol"
                      style={{
                        display: 'inline-block',
                        width: '15px',
                        height: '15px',
                        borderRadius: '50%',
                        backgroundColor: getFarolColor(analista.taxa_ocupacao || 0),
                        marginRight: '8px',
                      }}
                    ></span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Modal open={analistaModalOpen} onClose={() => setAnalistaModalOpen(false)}>
          <Box className="modal-box">
            <h2>Adicionar Analista</h2>
            <TextField
              label="Analista"
              select
              value={novoAnalista.analista_id}
              onChange={(e) =>
                setNovoAnalista({ ...novoAnalista, analista_id: e.target.value })
              }
              fullWidth
              margin="normal"
            >
              {todosAnalistas.map((analista) => (
                <MenuItem key={analista.id} value={analista.id}>
                  {analista.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="SLA Meta"
              type="number"
              value={novoAnalista.sla_meta}
              onChange={(e) => setNovoAnalista({ ...novoAnalista, sla_meta: e.target.value })}
              fullWidth
              margin="normal"
            />
            <div className="modal-actions">
              <Button
                variant="contained"
                onClick={handleAddAnalista}
                className="save-analista-button"
              >
                Adicionar
              </Button>
              <Button
                variant="outlined"
                onClick={() => setAnalistaModalOpen(false)}
                className="cancel-analista-button"
              >
                Cancelar
              </Button>
            </div>
          </Box>
        </Modal>
      </div>
    </div>
  );
}

export default DetalhesProcesso;
